<template>
  <div>
    <div class="details-component">
      <div class="details-component__header">
        <h1>{{ $t('users.details') }}</h1>
        <div class="details-component__header_right" v-if="!isDeleted() && $route.params.id">
          <a v-on:click="openPasswordSetForm" class="link link--orange" v-if="!selectedUser.managed_by_tenant">{{ $t('login.set_new_password') }}</a>
          <router-link :to="{name: 'edit-user', params: { id: selectedUser.id }}" class="link">
            {{ $t('common.change') }}&nbsp;<i class="icon icon--arrow"></i>
          </router-link>
          <a v-on:click="showDeletingConfirmation" class="link link--orange">{{ $t('common.delete') }}&nbsp;<i class="icon icon--cross"></i></a>
        </div>
      </div>


      <div class="details-component__add-new-form"
           v-if="isPasswordSetFormOpened"
           v-bind:class="{ active: isPasswordSetFormOpened }">
        <form class="form">
          <div class="form__field" :class="{'form__field_error' : errors.has('password')}">
            <label for="password" class="form__label">{{ $t('login.new_password') }}</label>
            <input
              id="password"
              type="password"
              class="form__credentials-input"
              v-model="form.password"
              :placeholder="$t('login.new_password')"
              name="password"
              v-validate="'required|min:6'"
              data-vv-validate-on="blur"
            />
          </div>

          <div class="form__buttons buttonset">
            <button
              type="submit"
              class="buttonset__button btn btn--main"
              v-on:click.prevent.stop="resetPassword()">
              {{ $t('common.save') }}
            </button>
          </div>
        </form>
      </div>

      <div class="details-component__content content">
        <table class="details-table no-col-padding fixed-table label-col-wide-150">
          <template v-for="partner in selectedUser.partner" v-if="isSuperAdmin()">
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('common.landlord') }}</td>
               <td class="details-table__col">
                  <router-link v-if="partner.landlord && partner.landlord.name" :to="{ name: 'landlord', params: { id: partner.landlord.id }, query: { keyword: partner.landlord.name } }" class="link link--blue">
                      {{ partner.landlord.name }}
                  </router-link>
                  <span v-else>&nbsp;</span>
              </td>
            </tr>
          </template>
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('common.name') }}</td>
            <td class="details-table__col bold">{{ selectedUser.name }}</td>
          </tr>
          <tr class="details-table__row" v-if="selectedUser.mobile">
            <td class="details-table__col details-table__col_label">{{ $t('common.phone') }}</td>
            <td class="details-table__col">{{ this.formatMobileNrForForm() }}</td>
          </tr>
          <tr class="details-table__row" v-if="selectedUser.email">
            <td class="details-table__col details-table__col_label">{{ $t('common.email') }}</td>
            <td class="details-table__col">{{ selectedUser.email }}</td>
          </tr>
          <template v-for="partner in selectedUser.partner" v-if="isLandlord()">
            <tr class="details-table__row">
              <td class="details-table__col details-table__col_label">{{ $t('common.tenant') }}</td>
              <td class="details-table__col">
                <router-link :to="{ name: 'tenant', params: { id: partner.id }, query: { keyword: partner.name } }" class="link link--blue">
                  {{ partner.name }}
                </router-link>
              </td>
            </tr>
          </template>
          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('common.created_at') }}</td>
            <td class="details-table__col">{{ selectedUser.created_at }}</td>
          </tr>

          <tr class="details-table__row auto-height" v-if="selectedUser.managed_by_tenant">
            <td class="details-table__col txt-to-left padding-t-4 always-cell" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input type="checkbox" id="tenant_can_manage_permit_expiration" disabled class="form__fieldset__checkbox" v-model="selectedUser.managed_by_tenant">
                <span v-model="selectedUser.managed_by_tenant" class="form__fieldset__custom-checkbox" disabled></span>
                <span class="custom-checkbox-label">{{ $t('add_tenant.managed_by_tenant') }}</span>
              </label>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelUserDelete" @confirmed="deleteUser">
      <h1 slot="header">{{ $t('common.deleting') }}: {{ selectedUser.name }} </h1>
      <p slot="body">{{ $t('users.confirm_delete') }}</p>
    </ConfirmationModal>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ userWarningHeader }}</h1>
      <p slot="body">{{ userWarningBody }}</p>
    </WarningModal>
  </div>
</template>

<script>

import { format } from 'libphonenumber-js';
import { mapGetters } from 'vuex';
import * as mTypes from 'store/mutation-types';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';

export default {
  components: {
    ConfirmationModal,
    WarningModal,
  },
  computed: mapGetters({
    selectedUser: 'users/user/user',
    userError: 'users/user/error',
    permits: 'landlord/permits/permits',
  }),
  props: [
    'selectedTenant',
  ],
  watch: {
    $route: 'fetchUser',
  },
  route: {
    canReuse: true,
  },
  created() {
    this.$store.commit(`users/user/${mTypes.CLEAR_STATE}`);
    if (this.$route.params.id) {
      this.fetchUser();
    }
  },
  methods: {
    isDeleted() {
      this.$emit('isDeleted', this.selectedUser.isDeleted);
      return this.selectedUser.isDeleted;
    },
    formatMobileNrForForm() {
      if (this.selectedUser.mobile && typeof this.selectedUser.mobile !== 'undefined') {
        return format({ country: this.selectedUser.mobileCountry, phone: this.selectedUser.mobile.toString() }, 'International_plaintext');
      }
      return this.selectedUser.mobile;
    },
    fetchUser() {
      if (this.$route.params.id && this.user.data) {
        this.$store.dispatch('users/user/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
          this.$emit('selectedTenant', this.selectedUser.partner[0].id);
        });
      }
    },
    showDeletingConfirmation() {
      this.showConfirmationModal = true;
    },
    closeWarningModal() {
      this.showWarningModal = false;
    },
    cancelUserDelete() {
      this.showConfirmationModal = false;
    },
    deleteUser() {
      this.showConfirmationModal = false;
      if (this.permits.items.length > 0) {
        this.userWarningHeader = this.$t('common.deleting');
        this.userWarningBody = this.$t('errors.permit_attached_to_user');
        this.showWarningModal = true;
        return;
      }
      this.$store.dispatch('users/user/delete', { id: this.selectedUser.id, user: this.user })
        .then(() => {
          if (this.userError) {
            this.userWarningHeader = this.$t('common.deleting');
            this.userWarningBody = this.$t('users.delete_error');
            this.showWarningModal = true;
          } else {
            this.$store.commit(`landlord/users/${mTypes.REMOVE_DATA}`, this.selectedUser.id);
            this.$router.push({ name: 'users' });
          }
        })
        .catch(() => {
          this.$store.commit(`users/user/${mTypes.DATA_ERROR}`, true);
        });
    },
    openPasswordSetForm() {
      this.isPasswordSetFormOpened = true;
    },
    resetPassword() {
      this.$validator.validateAll()
        .then((success) => {
          if (!success) {
            return;
          }
          this.form.partner_id = this.$route.params.id;
          this.$store.dispatch('users/user/setPassword', { userId: this.selectedUser.id, data: this.form, user: this.user }).then(() => {
            if (this.userError) {
              this.userWarningHeader = this.$t('accounts.set_password_error_header');
              this.userWarningBody = this.$t('accounts.set_password_error');
              this.showWarningModal = true;
            }
            this.isPasswordSetFormOpened = false;
          }).catch(() => {
            this.userWarningHeader = this.$t('accounts.set_password_error_header');
            this.userWarningBody = this.$t('accounts.set_password_error');
            this.showWarningModal = true;
          });
        });
    },
  },
  data() {
    return {
      isPasswordSetFormOpened: false,
      showConfirmationModal: false,
      showWarningModal: false,
      userWarningBody: null,
      userWarningHeader: null,
      form: {},
    };
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/blocks/details.scss';
.btn {
  min-width:100%;
}
.details-component__header_right {
  .link {
    padding-right: 10px;
    &:last-child {
      padding-right: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/users/UserDetails.vue